import { Validator } from "@pineapplelab/validators";
import { camelToSnakeCase, isArray, isNoEmptyArray, isNotEmptyString, isNullOrUndefined, isObject, snakeToCamelCase } from "@pineapplelab/util";
import { AppErrorStateMatcher } from "./errorStateMatcher";
import firebase from "firebase/compat/app";
import { DocumentReference, FieldValue, Timestamp } from "firebase/firestore";

export abstract class Base implements Base.IBase {
    __errorsByField: { [field: string]: string[] } = {};
    __errorByField: { [field: string]: string } = {};
    __errorByFieldByDecorator: { [field: string]: { [decorator: string]: string } } = {};
    __matcherByField: { [field: string]: AppErrorStateMatcher } = {};
    __errors: string[] = [];
    __hasError: boolean = false;

    /**
     *
     * All properties that start with '__' wont be mapped to the entity on save they will be removed!
     *
     * Default format is snake_case.
     *
     * @param camelCase - if set it to true the format of the property names will be camelCase
     *
     * @param {string[]} onlyFields - if you define some fields, only those will be mapped in the json
     *
     * @param {boolean} onlyFieldsAreRemovedInTheJson - if you set it to true, onlyFields will be removed from the json
     *
     */
    toJson(camelCase = false, onlyFields: string[] = null, onlyFieldsAreRemovedInTheJson = false) {
        if (camelCase) {
            return this.baseToJson(onlyFields, onlyFieldsAreRemovedInTheJson);
        }
        return this.toSnakeCaseJson(onlyFields, onlyFieldsAreRemovedInTheJson);
    }

    fromJson(json: { [key: string]: any }) {
        this.timestampToDate(json);
        this.fromJsonToBase(json);
        return this;
    }

    /**
     * The function converts timestamp values in a JSON object to Date objects.
     * @param json - The `json` parameter is an object that contains key-value pairs.
     */
    timestampToDate(json: { [key: string]: any }) {
        this.getMetadata()
            .map((meta) => camelToSnakeCase(meta))
            .forEach((prop: any) => {
                let dateInstance: Date = null;
                const timestampInstance = json[prop];

                if (timestampInstance instanceof Timestamp) {
                    dateInstance = timestampInstance.toDate();
                }

                if (typeof timestampInstance == 'string') {
                    dateInstance = new Date(timestampInstance);
                }

                if (typeof timestampInstance == 'object' && Boolean(timestampInstance?.seconds)) {
                    dateInstance = new Date(timestampInstance?.seconds * 1000);
                }
                // TODO: falta poner para el caso {seconds: millisecond:}
                json[prop] = dateInstance;
            });
    }

    /**
     * The function "getMetadata" returns an array of string values representing date properties, this mapping the firebase Timestamp and Date
     * @returns an array of strings.
     */
    public getMetadata(): string[] {
        const DATE_PROP_KEY = 'dateProperties';
        return Reflect.getMetadata(DATE_PROP_KEY, this) || [];
    }

    private baseToJson(onlyFields: string[] = null, onlyFieldsAreRemovedInTheJson = false) {
        const trueValues: any[] = [];
        const text = JSON.stringify(this, (key, value) => {
            if (this.isSensitiveValue(value)) {
                return `$__sensitiveValue${trueValues.push(value) - 1}`;
            }
            return value;
        });
        const json = JSON.parse(text, (key, value) => {
            if (isNotEmptyString(value) && value.startsWith('$__sensitiveValue')) {
                return trueValues[parseInt(value.slice(17), 10)];
            }
            return value;
        });
        this.removeAllPrivetProperties(json);
        // here reverse some firebase native classes

        if (!isNoEmptyArray(onlyFields)) {
            return json;
        }
        Object.keys(json).map((key) => {
            const isIncluded = onlyFields.includes(key);
            if ((!isIncluded && !onlyFieldsAreRemovedInTheJson) || (isIncluded && onlyFieldsAreRemovedInTheJson)) {
                delete json[key];
            }
        });
        return json;
    }

    private toSnakeCaseJson(onlyFields: string[] = null, onlyFieldsAreRemovedInTheJson = false) {
        const json = this.baseToJson(onlyFields, onlyFieldsAreRemovedInTheJson);
        this.toSnakeCaseAllSubObjects(json);
        return json;
    }

    private fromJsonToBase(json: { [key: string]: any }) {
        let result = this.convertKeysToCamelCase(json);
        Object.assign(this, result);
    }

    private toCamelCaseAllSubObjects(object: any) {
        this.modifyEveryKeyInAObject(object, snakeToCamelCase);
    }

    private toSnakeCaseAllSubObjects(object: any) {
        this.modifyEveryKeyInAObject(object, camelToSnakeCase);
    }

    private modifyEveryKeyInAObject(object: any, keyModifier: (key: string) => string) {
        this.doItForEveryKeyInAObject(object, (key, _object) => {
            const snakeKey = keyModifier(key);
            if (snakeKey === key) {
                return;
            }
            _object[snakeKey] = _object[key];
            delete _object[key];
        });
    }

    private removeAllPrivetProperties(object: any) {
        this.doItForEveryKeyInAObject(object, (key, _object) => {
            if (key.startsWith('__')) {
                delete _object[key];
            }
        });
    }

    private doItForEveryKeyInAObject(object: any, it: (key: string, object: any) => void, deepLevel = 0) {
        deepLevel++;
        if (deepLevel > DEEP_LEVEL_MAX || !isObject(object) || this.isSensitiveValue(object)) {
            return;
        }
        if (isArray(object as any[])) {
            return object.map((item: any) => this.doItForEveryKeyInAObject(item, it, deepLevel));
        }
        Object.keys(object).map((key) => {
            if (isObject(object[key])) {
                this.doItForEveryKeyInAObject(object[key], it, deepLevel);
            }
            it(key, object);
        });
    }

    private isSensitiveValue(object: any) {
        if (isNullOrUndefined(object)) {
            return false;
        }
        const types = [DocumentReference, FieldValue, AppErrorStateMatcher];
        return types.some((type) => object instanceof type);
    }

    isValid(witheList: string[] = []) {
        const result = Validator.validate(this);
        if (!isNoEmptyArray(witheList)) {
            return this.setErrorData(result);
        }
        witheList.map((field) => {
            delete result.fieldError[field];
            delete result.fieldErrors[field];
            delete result.fieldDecoratorError[field];
        });
        result.errors = [];
        Object.keys(result.fieldErrors).map((field) => result.errors.push(...result.fieldErrors[field]));
        result.valid = result.errors.length === 0;
        return this.setErrorData(result);
    }

    private setErrorData(result: any) {
        this.__hasError = !result.valid;
        this.__errorByField = result.fieldError;
        this.__errorsByField = result.fieldErrors;
        this.__errors = result.errors;
        this.__errorByFieldByDecorator = result.fieldDecoratorError;
        Object.keys(this.__errorByField).map((field) => {
            if (isNullOrUndefined(this.__matcherByField[field])) {
                this.__matcherByField[field] = new AppErrorStateMatcher(this, field);
            }
        });
        return !this.__hasError;
    }
    private convertKeysToCamelCase(obj: Record<string, any>): Record<string, any> {
        const newObj: Record<string, any> = {};

        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const camelCaseKey = this.snakeToCamel(key);
                newObj[camelCaseKey] = obj[key];
            }
        }
        return newObj;
    }

    private snakeToCamel(snakeStr: string): string {
        const words = snakeStr.split('_');
        const camelWords = [words[0]];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            camelWords.push(word.charAt(0).toUpperCase() + word.slice(1));
        }

        return camelWords.join('');
    }
}

export namespace Base {

  export interface IModelRelationship {
    id: string;
    reference: firebase.firestore.DocumentReference;
    name: string;
  };

  export interface IBase {
    __matcherByField: { [field: string]: AppErrorStateMatcher };
    __errorByField: { [field: string]: string };
    isValid: () => boolean;
    [key: string]: any;
  };

}

const DEEP_LEVEL_MAX = 10;
